import { useAuth } from "@/contexts/AuthContext";
import { useCallback, useEffect, useState } from "react";

// ─── Types ──────────────────────────────────────────────────────────────────

export interface Notification {
  id: string;
  type: string;
  channel: string;
  status: string;
  title: string | null;
  message: string;
  data: Record<string, unknown> | null;
  createdAt: string;
  readAt: string | null;
}

interface NotificationsResponse {
  notifications: Notification[];
  total: number;
  unread: number;
}

interface UseNotificationsReturn {
  notifications: Notification[];
  unreadCount: number;
  total: number;
  loading: boolean;
  error: string | null;
  markAsRead: (id: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  refresh: () => Promise<void>;
}

// ─── API Functions ──────────────────────────────────────────────────────────

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:3001";

async function fetchWithAuth(
  url: string,
  options: RequestInit = {},
): Promise<Response> {
  const token = localStorage.getItem("accessToken");

  return fetch(`${API_URL}/api${url}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers,
    },
  });
}

// ─── Hook ───────────────────────────────────────────────────────────────────

export function useNotifications(
  pollInterval = 60000, // Poll every 60 seconds by default
): UseNotificationsReturn {
  const { isAuthenticated } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch notifications
  const refresh = useCallback(async () => {
    if (!isAuthenticated) return;

    setLoading(true);
    setError(null);

    try {
      const response = await fetchWithAuth(
        "/notifications?channel=IN_APP&limit=20",
      );

      if (!response.ok) {
        throw new Error("Falha ao carregar notificações");
      }

      const data: NotificationsResponse = await response.json();
      setNotifications(data.notifications);
      setUnreadCount(data.unread);
      setTotal(data.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erro desconhecido");
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  // Mark single notification as read
  const markAsRead = useCallback(async (id: string) => {
    try {
      const response = await fetchWithAuth(`/notifications/${id}/read`, {
        method: "PATCH",
      });

      if (!response.ok) {
        throw new Error("Falha ao marcar como lida");
      }

      // Update local state
      setNotifications((prev) =>
        prev.map((n) =>
          n.id === id
            ? { ...n, readAt: new Date().toISOString(), status: "LIDO" }
            : n,
        ),
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (err) {
      console.error("Failed to mark notification as read:", err);
    }
  }, []);

  // Mark all as read
  const markAllAsRead = useCallback(async () => {
    try {
      const response = await fetchWithAuth("/notifications/read-all", {
        method: "PATCH",
      });

      if (!response.ok) {
        throw new Error("Falha ao marcar todas como lidas");
      }

      // Update local state
      setNotifications((prev) =>
        prev.map((n) => ({
          ...n,
          readAt: new Date().toISOString(),
          status: "LIDO",
        })),
      );
      setUnreadCount(0);
    } catch (err) {
      console.error("Failed to mark all notifications as read:", err);
    }
  }, []);

  // Initial fetch
  useEffect(() => {
    refresh();
  }, [refresh]);

  // Polling
  useEffect(() => {
    if (!isAuthenticated || pollInterval <= 0) return;

    const intervalId = setInterval(refresh, pollInterval);
    return () => clearInterval(intervalId);
  }, [isAuthenticated, pollInterval, refresh]);

  return {
    notifications,
    unreadCount,
    total,
    loading,
    error,
    markAsRead,
    markAllAsRead,
    refresh,
  };
}

export default useNotifications;
